"use strict";

/**
 * @typedef {Object} ArcGISMods
 * @property {typeof import("esri/map")} Map
 * @property {typeof import("esri/toolbars/draw")} Draw
 * @property {typeof import("esri/toolbars/edit")} Edit
 * @property {typeof import("dojo/_base/event")} event
 * @property {typeof import("esri/graphic")} Graphic
 * @property {typeof import("esri/symbols/SimpleMarkerSymbol")} SimpleMarkerSymbol
 * @property {typeof import("esri/symbols/SimpleLineSymbol")} SimpleLineSymbol
 * @property {typeof import("esri/symbols/SimpleFillSymbol")} SimpleFillSymbol
 * @property {typeof import("esri/geometry/Extent")} Extent
 * @property {typeof import("esri/layers/TileInfo")} TileInfo
 * @property {typeof import("esri/SpatialReference")} SpatialReference
 * @property {typeof import("esri/dijit/LayerList")} LayerList
 * @property {typeof import("esri/dijit/Search")} Search
 * @property {typeof import("esri/layers/OpenStreetMapLayer")} OpenStreetMapLayer
 * @property {typeof import("esri/layers/WMTSLayer")} WMTSLayer
 * @property {typeof import("esri/layers/WMTSLayerInfo")} WMTSLayerInfo
 * @property {typeof import("esri/geometry/webMercatorUtils")} webMercatorUtils
 * @property {typeof import("esri/geometry/geometryEngine")} geometryEngine
 * @property {typeof import("esri/tasks/GeometryService")} GeometryService
 * @property {typeof import("esri/tasks/ProjectParameters")} ProjectParameters
 * @property {typeof import("esri/tasks/AreasAndLengthsParameters")} AreasAndLengthsParameters
 * @property {typeof import("dojo/parser")} parser
 * @property {typeof import("dijit/registry")} registry
 * @property {typeof import("esri/graphicsUtils")} graphicsUtils
 * @property {typeof import("dojo/on")} on
 * @property {typeof import("esri/tasks/locator")} Locator
 * @property {typeof import("esri/layers/WMSLayer")} WMSLayer
 * @property {typeof import("esri/layers/WMSLayerInfo")} WMSLayerInfo
 * @property {typeof import("esri/geometry/Polygon")} Polygon
 * @property {typeof import("dojo/i18n!esri/nls/jsapi")} bundle
 */

export class GisMap {


	config = {};
	/** @type {ArcGISMods|null} */
	arcgis = null;

	selectedPolygon = '';
	selectedGraphic = null
	startMapExtent = null;
	initZoom = 9;
	map = null;
	lastCenter = null;
	newCenter = null;
	slovakiaMapExtent = null;
	slovakiaGraphic = null;
	geometryService = null;
	calculateSelectedPolygon = null;
	areaCalculationFinish = false;
	areaCalculationError = false;
	isIntersecting = false;
	areaNotMatchConditions = false;
	regionNotMatchConditions = false;
	maxAreaSize = 100000;
	maxAreaLength = 20000;
	pendingExternalRequests = 0;
	loaderOverlayEl = null;

	// Toolbar
	toolbar = null;
	editToolbar = null;
	searchWidget = null

	// Toolbar events
	eventClickEdit = null;
	eventGraphicMoveStop;
	eventVertexMoveStop;
	eventScaleStop;

	argisMapEl = null;
	btnAddPolygonEl = null;
	btnRemovePolygonEl = null;
	markedAreaSectionEl = null;
	markedAreaTextContainerEl = null;
	selectedGeoAreaSizeEl = null;
	selectedGeoAreaLengthEl = null;
	selectedPolygonEl = null;
	mainGfHiddenInputEl = null;
	isValidFieldEl = null;


	constructor(config, arcGisModules) {
		this.config = config;
		this.arcgis = arcGisModules;
		this.init()

	}

	init() {
		//console.log('GisMap init')

		this.argisMapEl = document.getElementById("gf-gis-addon-argis-map")
		this.btnAddPolygonEl = document.getElementById("gf-gis-addon-btn-add-polygon")
		this.btnRemovePolygonEl = document.getElementById("gf-gis-addon-btn-remove-polygon")

		this.markedAreaSectionEl = document.getElementById("gf-gis-addon-selected-area-section")
		this.markedAreaTextContainerEl = document.getElementById("gf-gis-addon-marked-area-text-container")

		this.selectedGeoAreaSizeEl = document.getElementById("gf-gis-addon-selected-geo-area-size")
		this.selectedGeoAreaLengthEl = document.getElementById("gf-gis-addon-selected-geo-area-length")
		this.selectedPolygonEl = document.getElementById("gf-gis-addon-selected-polygon")
		this.mainGfHiddenInputEl = document.querySelector(".gf-gis-addon-main-input")

		this.isValidFieldEl = document.getElementById("gf-gis-addon-is-valid-field")
		this.arcgisErrorEl = document.getElementById("gf-gis-addon-arcgis-error")

		if (!this.argisMapEl
			|| !this.btnAddPolygonEl
			|| !this.btnRemovePolygonEl
			|| !this.selectedGeoAreaSizeEl
			|| !this.selectedGeoAreaLengthEl
			|| !this.selectedPolygonEl
			|| !this.mainGfHiddenInputEl
			|| !this.isValidFieldEl) {
			return;
		}

		// Get max are size from hidden input
		const maxAreaSizeEl = document.getElementById("gf-gis-addon-max-area-size")
		if (maxAreaSizeEl !== null) {
			this.maxAreaSize = parseFloat(maxAreaSizeEl.value)
			//console.log('Max area size:', this.maxAreaSize)
		}

		const maxAreaLengthEl = document.getElementById("gf-gis-addon-max-area-length")
		if (maxAreaLengthEl !== null) {
			this.maxAreaLength = parseFloat(maxAreaLengthEl.value)
			//console.log('Max area length:', this.maxAreaLength)
		}

		this.slovakiaGraphic = new this.arcgis.Graphic(new this.arcgis.Polygon(this.config.slovakiaArcGisJson.geometry), new this.arcgis.SimpleFillSymbol());
		this.slovakiaMapExtent = this.arcgis.graphicsUtils.graphicsExtent([this.slovakiaGraphic]);

		// Calculate selected polygon debounced function
		this.calculateSelectedPolygon = this.debounce((geometry) => {
			this.doPolygonCalculation(geometry)
		}, 500)


		// Set test data only for debug
		//this.setTestData()

		// Load selected polygon from (html element)
		if (this.mainGfHiddenInputEl !== null && this.mainGfHiddenInputEl.value !== '') {
			//console.log('Selected polygon:', this.mainGfHiddenInputEl.value);
			this.selectedPolygon = this.mainGfHiddenInputEl.value;
		}

		// Init Map
		if (this.selectedPolygon) {
			this.beginExternalRequest('Nacitavam ulozeny polygon...');
			try {
				const geographicPolygon = new this.arcgis.Polygon({
					rings: JSON.parse(this.selectedPolygon),
					spatialReference: {wkid: this.config.wkid}
				});
				const mapPolygon = this.toMapGeometry(geographicPolygon);
				this.selectedGraphic = new this.arcgis.Graphic(mapPolygon, new this.arcgis.SimpleFillSymbol());
					this.startMapExtent = this.arcgis.graphicsUtils.graphicsExtent([this.selectedGraphic]);
					this.initMap();
					this.endExternalRequest();
			} catch (e) {
					console.error(e);
					this.endExternalRequest();
					this.serAlertVisibility('alert-danger', true, 'arcgis-connection-error');
					this.setValidField(0);
					this.setArcgisError(true);
					this.startMapExtent = this.arcgis.graphicsUtils.graphicsExtent([this.slovakiaGraphic]);
					this.initMap();
			}
		} else {
			this.startMapExtent = this.arcgis.graphicsUtils.graphicsExtent([this.slovakiaGraphic]);

			this.initMap();
		}


		this.bindCustomUiControls()

	}

	bindCustomUiControls() {

		// Map layers switcher
		const mapLayers = document.querySelectorAll(".map-layer");
		mapLayers.forEach(mapLayer => {
			mapLayer.addEventListener('change', (e) => {
				//console.log('Change map layer to:', e.target.value);
				this.changeMapLayer(e.target.value);
			})
		})

		// Activate toolbar, when click to button for add polygon
		if (this._onAddPolygonClick) {
			this.btnAddPolygonEl.removeEventListener("click", this._onAddPolygonClick);
		}
		this._onAddPolygonClick = () => {
			this.toolbar.activate(this.arcgis.Draw["POLYGON"]);
			return false;
		};
		this.btnAddPolygonEl.addEventListener("click", this._onAddPolygonClick);

		// Remove polygon on button click
		if (this._onRemovePolygonClick) {
			this.btnRemovePolygonEl.removeEventListener("click", this._onRemovePolygonClick);
		}
		this._onRemovePolygonClick = () => {
			// This calls map.graphics.on("graphics-clear" event
			this.map.graphics.clear();
			return true;
		};
		this.btnRemovePolygonEl.addEventListener("click", this._onRemovePolygonClick);

	}

	initMap(initZoom) {

		// Custom preklady
		// esri.bundle.toolbars.draw.start
		this.arcgis.bundle.toolbars.draw.start = this.argisMapEl.dataset.toolbarStratDrawText;
		this.arcgis.bundle.toolbars.draw.resume = this.argisMapEl.dataset.toolbarResumeDrawText;
		this.arcgis.bundle.toolbars.draw.complete = this.argisMapEl.dataset.toolbarCompleteDrawText;


		//Define OpenStreetLayer
		const openStreetMapLayer = this.getOpenStreetMapLayer();
		const zbgisMapLayer = this.getZbgisMapLayer();
		const wmsEsknMapLayer = this.getWmsEsknMapLayer();

		//start map options
		const mapOptions = {
			logo: false,
			extent: this.startMapExtent,
			minZoom: 9
		};
		if (initZoom != null) {
			mapOptions.zoom = this.initZoom;
		}

		/** @type {ArcGISMods.Map} */
		if (this.map !== null) {
			//console.log('Destroy map');
			this.map.destroy();
		}
		this.map = new this.arcgis.Map("gf-gis-addon-argis-map", mapOptions);


		// Toolbars
		this.editToolbar = new this.arcgis.Edit(this.map);
		this.toolbar = new this.arcgis.Draw(this.map);

		// Register load event
		this.map.on('load', (event) => {
			this.onMapLoad(event)
		});

		// Register extent change event
		this.map.on('extent-change', (event) => {
			this.fixMapExtent(event)
		});

		// Add map layers
		this.map.removeAllLayers();
		this.map.addLayer(openStreetMapLayer, 0);
		this.map.addLayer(zbgisMapLayer, 1);
		this.map.addLayer(wmsEsknMapLayer, 2);
		this.map.getLayer(this.map.layerIds[1]).hide();


		// Deactivate the toolbar when you click outside a graphic
		this.map.on("click", (evt) => {
			//console.log('Map click (outside)');
			this.editToolbar.deactivate();
			if (this.selectedGraphic !== '' && this.selectedGraphic !== null) {
				// When finished editing, calculate polygon, if any
				this.calculateSelectedPolygon(this.selectedGraphic.geometry);
			}
		});


		// Register graphics-clear (must be registered after layouts)
		this.map.graphics.on("graphics-clear", () => {
			this.doCleanRoutines();
		})

		// If exist polygon from DB / hidden input, add it to map and activate to edit
		if (this.selectedPolygon !== null && this.selectedPolygon !== '') {
			// addDrawnPolygonToMap handles graphics.add and calculateSelectedPolygon internally
			this.addDrawnPolygonToMap(this.selectedGraphic.geometry, false);
		}

	}

	getZbgisMapLayer() {
		//Define WMS Zbgis Layer
		const resourceInfoWmsZbgis = {
			extent: new this.arcgis.Extent(15.0, 46.0, 24.0, 51.0, {
				wkid: this.config.wkid
			}),
			layerInfos: []
		};

		return new this.arcgis.WMSLayer('https://zbgisws.skgeodesy.sk/zbgis_wms_featureinfo/service.svc/get', {
			resourceInfo: resourceInfoWmsZbgis,
			visibleLayers: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20',
				'21', '22', '23', '24', '25', '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36', '37', '38', '39', '40',
				'41', '42', '43', '44', '45', '46', '47', '48', '49', '50', '51', '52', '53', '54', '55', '56', '57', '58', '59', '60',
				'61', '62', '63', '64', '65', '66', '67', '68', '69', '70', '71', '72', '73', '74', '75', '76', '77', '78', '79', '80',
				'81', '82', '83', '84', '85', '86', '87', '88', '89', '90', '91', '93', '96', '98', '99', '100', '101',
				'103', '104', '105', '106', '108', '110', '111', '112', '113', '115', '116', '117', '118', '119', '120', '122', '123',
				'124', '125', '126', '127', '129', '131', '132', '133', '134', '135', '136', '137', '138', '139', '140', '141', '142',
				'143', '144', '145', '146', '149', '151', '153', '154', '155', '156', '157', '158', '160', '161', '162', '163', '164',
				'165', '167']
		});

	}

	getOpenStreetMapLayer() {
		return new this.arcgis.OpenStreetMapLayer();
	}

	getWmsEsknMapLayer() {
		//Define WMS ESKN Layer
		const resourceInfoWmsEskn = {
			extent: new this.arcgis.Extent(15.0, 46.0, 24.0, 51.0, {
				wkid: this.config.wkid
			}),
			layerInfos: []
		};
		return new this.arcgis.WMSLayer('https://kataster.skgeodesy.sk/eskn/services/NR/kn_wms_norm/MapServer/WMSServer', {
			resourceInfo: resourceInfoWmsEskn,
			visibleLayers: ['5', '6', '7', '8', '10', '13', '14', '15']
		});
	}

	changeMapLayer(layerType) {
		switch (layerType) {
			case 'zbgis':
				this.map.getLayer(this.map.layerIds[0]).hide();
				this.map.getLayer(this.map.layerIds[1]).show();
				break;
			case 'openstreetmaps':
				this.map.getLayer(this.map.layerIds[0]).show();
				this.map.getLayer(this.map.layerIds[1]).hide();
				break;
		}
	}

	onMapLoad(event) {
		console.log('Map loaded');
		this.createSearchWidget();
		this.createToolbar();
	}


	/**
	 * Create a search widget for search places
	 */
	createSearchWidget() {

		if (this.searchWidget !== null) {
			this.searchWidget.destroy();
		}

		this.searchWidget = new this.arcgis.Search({
			map: this.map,
			enableInfoWindow: false,
			popupEnabled: false,
			sources: [{
				countryCode: "SK",
				locator: new this.arcgis.Locator("//geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer"),
				singleLineFieldName: "SingleLine",
				outFields: ["Addr_type"],
				placeholder: this.argisMapEl.dataset.searchWidgetText
			}]
		}, "search");
		this.searchWidget.startup();
	}

	searchLocation(text) {
		if (this.searchWidget !== null && text) {
			this.searchWidget.search(text);
		}
	}

	// region Toolbar

	/**
	 * Create a toolbar for editing polygon
	 */
	createToolbar() {
		this.toolbar.on("draw-end", (evt) => {
			//console.log('Draw end');
			this.addDrawnPolygonToMap(evt.geometry);
		});
	}

	/**
	 * Add new drawn polygon to map
	 * @param geometry
	 * @param activateToolbar
	 */
	addDrawnPolygonToMap(geometry, activateToolbar = true) {
		this.map.graphics.clear();
		this.toolbar.deactivate();
		this.map.showZoomSlider();
		const symbol = new this.arcgis.SimpleFillSymbol();
		this.selectedGraphic = new this.arcgis.Graphic(geometry, symbol);
		this.selectedGraphic.setAttributes({id: "my-polygon", name: "Polygon"});
		this.map.graphics.add(this.selectedGraphic);
		this.setPolygonGraphicClickEvent();
		if (activateToolbar) {
			this.activateToolbar(this.selectedGraphic);
		} else {
			this.setAddPolygonButtonVisibility(false);
			this.setRemovePolygonButtonVisibility(true);
		}
		this.calculateSelectedPolygon(geometry);

	}

	setPolygonGraphicClickEvent() {
		// Activate the toolbar when you click on a graphic

		if (this.eventClickEdit !== undefined && this.eventClickEdit !== null) {
			this.eventClickEdit.remove();
		}

		this.eventClickEdit = this.map.graphics.on("click", (evt) => {
			const clickedGraphic = evt.graphic;
			if (clickedGraphic && clickedGraphic.attributes && clickedGraphic.attributes.id === "my-polygon") {
				// alert("Klikol si na bod: " + clickedGraphic.attributes.name);
				this.activateToolbar(evt.graphic);
			}
			this.arcgis.event.stop(evt);
		});

		// console.log('Set polygon graphic click event');
		// console.log(this.map.graphics.graphics);
	}


	/**
	 * Activate editing mode for some polygon
	 * @param graphic
	 */
	activateToolbar(graphic) {

		// Change buttons state
		this.setAddPolygonButtonVisibility(false);
		this.setRemovePolygonButtonVisibility(true);

		let tool = 0;
		tool = tool | this.arcgis.Edit.EDIT_VERTICES;
		tool = tool | this.arcgis.Edit.MOVE;
		tool = tool | this.arcgis.Edit.SCALE;
		tool = tool | this.arcgis.Edit.ROTATE;

		//specify toolbar options
		const options = {
			allowAddVertices: true,
			allowDeleteVertices: true,
			uniformScaling: true
		};
		this.editToolbar.activate(tool, graphic, options);

		this.eventGraphicMoveStop = this.editToolbar.on("graphic-move-stop", (evt) => {
			this.calculateSelectedPolygon(graphic.geometry);
			this.clearErrorMessages();
		});

		this.eventVertexMoveStop = this.editToolbar.on("vertex-move-stop", (evt) => {
			this.calculateSelectedPolygon(graphic.geometry);
			this.clearErrorMessages();
		});

		this.eventScaleStop = this.editToolbar.on("scale-stop", (evt) => {
			this.calculateSelectedPolygon(graphic.geometry);
			this.clearErrorMessages();
		});
	}

	/**
	 * Set alert visibility
	 *
	 * Examples:
	 * Alert type => .alert.alert-danger
	 * Alert type + Alert Problem => .alert.alert-danger.area-under
	 * @param alertType
	 * @param visible
	 * @param alertProblem
	 */
	serAlertVisibility(alertType, visible, alertProblem = null,) {

		let alerts;
		if (alertProblem === null) {
			alerts = document.querySelectorAll('.alert.' + alertType)
		} else {
			alerts = document.querySelectorAll('.alert.' + alertType + '.' + alertProblem)
		}

		alerts.forEach(alert => {
			alert.style.display = visible ? 'block' : 'none';
		})


	}

	setAddPolygonButtonVisibility(visible) {
		if (visible) {
			this.btnAddPolygonEl.style.display = "block";
		} else {
			this.btnAddPolygonEl.style.display = "none";
		}
	}

	setRemovePolygonButtonVisibility(visible) {
		if (visible) {
			this.btnRemovePolygonEl.style.display = "block";
		} else {
			this.btnRemovePolygonEl.style.display = "none";
		}
	}

	// endregion


	/**
	 *  Handle success result from polygon area calculation
	 *
	 * @param result
	 */
	areaAndLengthSuccessCallback(result) {
		//console.log('areaAndLengthSuccessCallback');
		//console.log(result);
		const geoAreaValue = parseFloat(result.areas[0].toFixed(3))
		const geoLengthValue = parseFloat(result.lengths[0].toFixed(3))

		this.setGeoAreaSize(geoAreaValue);
		this.setGeoAreaLength(geoLengthValue);
		this.setMarkedAreaSectionVisibility(true)
		this.setMarkedAreaValues(geoAreaValue, geoLengthValue)

		// Plocha musi byt mensia ako maximalne povolena
		if (geoAreaValue > this.maxAreaSize) {
			//console.log('Geo area size > Max Geo area size');
			this.serAlertVisibility('alert-danger', true, 'area-over')
			this.serAlertVisibility('alert-danger', false, 'area-under')

			this.areaNotMatchConditions = true;
			// Obvod perimetru musi byt mensi ako maximalne povolena hodnota
		} else if (geoLengthValue > this.maxAreaLength) {
			//console.log('Geo area length > Max Geo area length');
			this.serAlertVisibility('alert-danger', true, 'area-over')
			this.serAlertVisibility('alert-danger', false, 'area-under')
			this.areaNotMatchConditions = true;
			// Plocha musi byt vacsia ako 0
		} else if (geoAreaValue <= 0) {
			this.serAlertVisibility('alert-danger', false, 'area-over')
			this.serAlertVisibility('alert-danger', true, 'area-under')
			this.areaNotMatchConditions = true;
		} else {
			this.serAlertVisibility('alert-danger', false, 'area-over')
			this.serAlertVisibility('alert-danger', false, 'area-under')
			this.areaNotMatchConditions = false;
		}


		this.serAlertVisibility('alert-danger', false, 'arcgis-connection-error')
		this.setArcgisError(false);
		this.areaCalculationFinish = true;
		this.setValidField()
	}


	/**
	*   Handle error from ArcGIS connection or other errors during area calculation
	*/
	projectErorCallback() {
	    this.serAlertVisibility('alert-danger', true, 'arcgis-connection-error');
        this.areaCalculationFinish = true;
        this.areaCalculationError = true;
        this.setValidField(0);
        this.setArcgisError(true);
	}

	/**
	 *  Handle error result from polygon area calculation
	 */
	areaAndLengthErrorCallback() {
		this.setGeoAreaSize('-')
		this.setGeoAreaLength('-')
		this.regionNotMatchConditions = true;
		this.areaNotMatchConditions = true;
		this.projectErorCallback();
	}


	doPolygonCalculation(geometry) {
		//console.log('Do polygon calculation');
		this.areaCalculationFinish = false;
		this.beginExternalRequest('Spracúvam polygon a kalkulujem plochu...');

		// Zadaný polygón má nesprávny tvar (niektoré hrany sa pretínaju)
		if (geometry.isSelfIntersecting()) {
			this.serAlertVisibility('alert-danger', true, 'polygon-intersect');
			this.isIntersecting = true;
		} else {
			this.serAlertVisibility('alert-danger', false, 'polygon-intersect');
			this.isIntersecting = false;
		}

		try {
			const geographicPolygon = this.toGeographicGeometry(geometry);
			if (!geographicPolygon) {
				throw new Error('Polygon projection failed');
			}

			this.setSelectedPolygon(JSON.stringify(geographicPolygon.rings))

			if (this.arcgis.geometryEngine.within(geographicPolygon, this.slovakiaGraphic.geometry)) {
				this.serAlertVisibility('alert-success', true, 'region');
				this.serAlertVisibility('alert-danger', false, 'region');
				this.regionNotMatchConditions = false
			} else {
				this.serAlertVisibility('alert-danger', true, 'region');
				this.serAlertVisibility('alert-success', false, 'region');
				this.regionNotMatchConditions = true
			}

			const simplified = this.arcgis.geometryEngine.simplify(geographicPolygon) || geographicPolygon;
			const area = Math.abs(this.arcgis.geometryEngine.geodesicArea(simplified, 'square-meters'));
			const length = Math.abs(this.arcgis.geometryEngine.geodesicLength(simplified, 'meters'));
			this.areaAndLengthSuccessCallback({areas: [area], lengths: [length]});
		} catch (e) {
			console.error(e);
			this.areaAndLengthErrorCallback()
		} finally {
			this.endExternalRequest();
		}

	}

	doCleanRoutines() {
		//console.log('Do clean routines');
		// Remove events on deleted polygon
		if (this.eventClickEdit !== undefined && this.eventClickEdit !== null) {
			this.eventClickEdit.remove();
		}
		if (this.eventGraphicMoveStop !== undefined && this.eventGraphicMoveStop !== null) {
			this.eventGraphicMoveStop.remove();
		}
		if (this.eventVertexMoveStop !== undefined && this.eventVertexMoveStop !== null) {
			this.eventVertexMoveStop.remove();
		}
		if (this.eventScaleStop !== undefined && this.eventScaleStop !== null) {
			this.eventScaleStop.remove();
		}

		// Reset buttons states
		this.setAddPolygonButtonVisibility(true);
		this.setRemovePolygonButtonVisibility(false);

		// Hide all messages
		this.serAlertVisibility('alert-success', false);
		this.serAlertVisibility('alert-danger', false);

		// Reset inputs
		this.resetSelectedGeoAreaSizeInput()
		this.selectedGeoAreaLengthEl.value = '';
		this.resetSelectedPolygonInput()

		// Clear marked area section
		this.setMarkedAreaSectionVisibility(false)
		this.setMarkedAreaValues(null, null)
		this.setValidField(0)
		this.setArcgisError(false);


		this.toolbar.deactivate();
		this.selectedGraphic = null;

	}


	resetSelectedGeoAreaSizeInput() {
		this.selectedGeoAreaSizeEl.value = '';
		this.selectedGeoAreaSizeEl.classList.remove('is-invalid');
	}
	
	/**
	 * Function clears all error messages when the polygon is changed
	 */
	clearErrorMessages() {
		this.serAlertVisibility('alert-success', false, 'region');
		this.serAlertVisibility('alert-danger', false, 'region');
		this.serAlertVisibility('alert-danger', false, 'area-over');
		this.serAlertVisibility('alert-danger', false, 'area-under');
	}


	/**
	 *  If a user moves a map with a mouse far from Slovakia, return moves little back
	 *
	 * @param event
	 */
	fixMapExtent(event) {
		const currentCenter = event.extent.getCenter();
		const currentCenterConverted = this.toGeographicGeometry(currentCenter);
		if (this.slovakiaMapExtent && currentCenterConverted && !this.slovakiaMapExtent.contains(currentCenterConverted) && event.delta && event.delta.x !== 0 && event.delta.y !== 0) {
			if (this.lastCenter) {
				this.map.centerAt(this.lastCenter);
			}
		} else {
			this.lastCenter = currentCenter;
		}
	}


	/**
	 * Debounce funkcia s podporou immediate volania
	 * @param {Function} func - Funkcia, ktorú chceš debounce-núť
	 * @param {number} wait - Čas v ms, počas ktorého sa odďaľuje vykonanie
	 * @param {boolean} [immediate=false] - Ak true, spustí hneď a potom čaká
	 * @returns {Function}
	 */
	debounce(func, wait, immediate = false) {
		let timeout;

		return function (...args) {
			const context = this;

			const later = () => {
				timeout = null;
				if (!immediate) func.apply(context, args);
			};

			const callNow = immediate && !timeout;
			clearTimeout(timeout);
			timeout = setTimeout(later, wait);

			if (callNow) func.apply(context, args);
		};
	};

	/**
	 * Format number
	 * @param number
	 * @param decimals
	 * @param decPoint
	 * @param thousandsSep
	 * @returns {string}
	 */
	formatNumber(number, decimals, decPoint, thousandsSep) {
		const numberFixed = number.toFixed(decimals);

		let numberStr = numberFixed.toString();
		numberStr += '';
		const x = numberStr.split('.');
		let x1 = x[0];
		const x2 = x.length > 1 ? decPoint + x[1] : '';
		const rgx = /(\d+)(\d{3})/;

		while (rgx.test(x1))
			x1 = x1.replace(rgx, '$1' + thousandsSep + '$2');

		return x1 + x2;
	}

	/**
	 *  Zoom to some polygon (etc. when selected cadastral area from another dropdown field)
	 *
	 * @param polygon - JSON in rings ArcGIS JSON format
	 */
	zoomToGraphics(polygon) {
		const poly = new this.arcgis.Polygon({
			rings: JSON.parse(polygon),
			spatialReference: {wkid: this.config.wkid}
		})
		const mapPolygon = this.toMapGeometry(poly);
		const graphic = new this.arcgis.Graphic(mapPolygon, new this.arcgis.SimpleFillSymbol());
		const extent = this.arcgis.graphicsUtils.graphicsExtent([graphic]);
		this.map.setExtent(extent);
	};

	/**
	 *  konvertuje geometriu do geografickej projekcie (WGS84), ak je v inej projekcii, inak vrati original
	 * @param {*} geometry 
	 * @returns 
	 */
	toGeographicGeometry(geometry) {
		if (!geometry) {
			return null;
		}

		const wkid = geometry.spatialReference && geometry.spatialReference.wkid;
		if (wkid === 102100 || wkid === 3857 || wkid === 102113) {
			return this.arcgis.webMercatorUtils.webMercatorToGeographic(geometry);
		}

		return geometry;
	}

	/**
	 *  konvertuje geometriu do projekcie mapy (Web Mercator), ak je v geografickej projekcii (WGS84), inak vrati original
	 * @param {*} geometry 
	 * @returns 
	 */
	toMapGeometry(geometry) {
		if (!geometry) {
			return null;
		}

		const wkid = geometry.spatialReference && geometry.spatialReference.wkid;
		if (wkid === 4326) {
			return this.arcgis.webMercatorUtils.geographicToWebMercator(geometry);
		}

		return geometry;
	}

	/**
	 * Set validate field value in DOM element based on conditions
	 */
	setValidField(forceValue = null) {

		if (forceValue !== null) {
			this.isValidFieldEl.setAttribute('value', forceValue);
			return;
		}

		//console.log(`Set valid field => areaNotMatchConditions: ${this.areaNotMatchConditions} | regionNotMatchConditions: ${this.regionNotMatchConditions} | isIntersecting: ${this.isIntersecting}`);
		if (this.areaNotMatchConditions === false
			&& this.regionNotMatchConditions === false
			&& this.isIntersecting === false
		) {
			this.isValidFieldEl.setAttribute('value', 1);
		} else {
			this.isValidFieldEl.setAttribute('value', 0);
		}

	}

	setArcgisError(isError) {
		if (this.arcgisErrorEl !== null && this.arcgisErrorEl !== undefined) {
			this.arcgisErrorEl.value = isError ? '1' : '0';
		}
	}

	setSelectedPolygon(value) {

		// Set textarea value
		if (this.selectedPolygonEl !== null) {
			this.selectedPolygonEl.setAttribute('value', value);
			this.selectedPolygonEl.innerText = value
			//this.selectedPolygonEl.value = value
		}

		// Set hidden input for GF value (important for validation)
		if (this.mainGfHiddenInputEl !== null) {
			this.mainGfHiddenInputEl.setAttribute('value', value);
		}
	}

	resetSelectedPolygonInput() {

		// Reset textarea value
		if (this.selectedPolygonEl !== null) {
			// this.selectedPolygonEl.value = '';
			this.selectedPolygonEl.setAttribute('value', '');
			this.selectedPolygonEl.innerText = '';
			this.selectedPolygonEl.classList.remove('is-invalid');
		}

		// Reset hidden input for GF value (important for validation)
		if (this.mainGfHiddenInputEl !== null) {
			this.mainGfHiddenInputEl.setAttribute('value', '');
		}
	}

	setGeoAreaSize(value) {
		if (this.selectedGeoAreaSizeEl !== null) {
			this.selectedGeoAreaSizeEl.value = value;
		}
	}

	setGeoAreaLength(value) {
		if (this.selectedGeoAreaLengthEl !== null) {
			this.selectedGeoAreaLengthEl.value = value;
		}
	}

	setMarkedAreaSectionVisibility(visibility) {
		if (this.markedAreaSectionEl !== null) {
			this.markedAreaSectionEl.style.display = visibility ? 'block' : 'none';
		}
	}

	setMarkedAreaValues(geoAreaValue, geoLengthValue) {
		if (this.markedAreaTextContainerEl !== null) {

			if (geoAreaValue === null || geoLengthValue === null) {
				this.markedAreaTextContainerEl.innerHTML = ''
			} else {

				const formatedGeoArea = this.formatNumber(geoAreaValue, 0, ',', ' ');
				const formatedGeoLength = this.formatNumber(geoLengthValue, 0, ',', ' ');
				let resulPlaceholder = this.markedAreaTextContainerEl.dataset.resultTextPlaceholder;
				const resultTextAreaSizeKey = this.markedAreaTextContainerEl.dataset.resultTextAreaSizeKey;
				const resultTextAreaLengthKey = this.markedAreaTextContainerEl.dataset.resultTextAreaLengthKey;
				resulPlaceholder = resulPlaceholder.replace(resultTextAreaSizeKey, formatedGeoArea);
				resulPlaceholder = resulPlaceholder.replace(resultTextAreaLengthKey, formatedGeoLength);
				this.markedAreaTextContainerEl.innerHTML = resulPlaceholder
			}
		}
	}

	ensureMapLoader() {
		if (!this.argisMapEl) {
			return null;
		}

		const wrapper = this.argisMapEl.parentElement;
		if (!wrapper) {
			return null;
		}

		if (!this.loaderOverlayEl) {
			this.loaderOverlayEl = document.createElement('div');
			this.loaderOverlayEl.className = 'gf-gis-loader-overlay map-loader';
			this.loaderOverlayEl.innerHTML = '<div class="gf-gis-loader-spinner" aria-hidden="true"></div><div class="gf-gis-loader-text">Komunikujem s externou GIS sluzbou...</div>';
			wrapper.style.position = 'relative';
			wrapper.appendChild(this.loaderOverlayEl);
		}

		return this.loaderOverlayEl;
	}

	beginExternalRequest(message = null) {
		this.pendingExternalRequests += 1;
		const overlay = this.ensureMapLoader();
		if (!overlay) {
			return;
		}

		const textEl = overlay.querySelector('.gf-gis-loader-text');
		if (textEl && message !== null) {
			textEl.textContent = message;
		}

		overlay.style.display = 'flex';
	}

	endExternalRequest() {
		this.pendingExternalRequests = Math.max(0, this.pendingExternalRequests - 1);
		if (this.pendingExternalRequests === 0 && this.loaderOverlayEl) {
			this.loaderOverlayEl.style.display = 'none';
		}
	}

	/**
	 * Set test data
	 */
	setTestData() {
		this.setSelectedPolygon('[[[19.6326140168803,48.80718299354121],[19.6321848638213,48.80706993781342],[19.632270694253442,48.80729604842248],[19.6326140168803,48.80718299354121]]]')
	}


}

